import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { ADMIN_NAV } from "./_nav";
import { api, errFrom, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Download, Mail, RefreshCw, CalendarRange } from "lucide-react";

function toLocalISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function defaultRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 6); // inclusive 7-day window ending today
  return { start: toLocalISO(start), end: toLocalISO(end) };
}

export default function AdminReports() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [{ start, end }, setRange] = useState(defaultRange());

  function buildParams() {
    const p = {};
    if (start) p.start_date = start;
    if (end) p.end_date = end;
    return p;
  }

  async function load() {
    if (start && end && start > end) {
      toast.error("Start date must be on or before end date");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/admin/weekly-report", { params: buildParams() });
      setReport(data);
    }
    catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }
  // Initial load only — subsequent loads are explicit via Apply button
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function sendEmail() {
    setSending(true);
    try {
      const { data } = await api.post("/admin/weekly-report/send", null, { params: buildParams() });
      if (data.ok) toast.success(`Report emailed to ${data.sent_to}`);
      else toast.error("Failed to send email. Check SMTP settings.");
    } catch (e) { toast.error(errFrom(e)); }
    finally { setSending(false); }
  }

  async function downloadCsv() {
    try {
      const qs = new URLSearchParams(buildParams()).toString();
      const r = await fetch(`${API}/admin/weekly-report.csv${qs ? `?${qs}` : ""}`, { credentials: "include" });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `report-${start || "start"}-to-${end || "end"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  }

  function applyPreset(days) {
    const endD = new Date();
    const startD = new Date(endD);
    startD.setDate(endD.getDate() - (days - 1));
    setRange({ start: toLocalISO(startD), end: toLocalISO(endD) });
  }

  return (
    <AppShell nav={ADMIN_NAV} title="Reports" subtitle={report?.label || "Custom range"}>
      {/* Date range filter */}
      <div className="brand-surface rounded-2xl p-4 sm:p-5 mb-4" data-testid="report-filters">
        <div className="flex items-center gap-2 mb-3">
          <CalendarRange size={16} className="text-[#FF3B30]" />
          <div className="text-overline">Date range</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Start date</Label>
            <Input
              type="date"
              value={start}
              max={end || undefined}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="mt-2 bg-[#0A0A0A] border-white/10 h-11"
              data-testid="report-start-date"
            />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">End date</Label>
            <Input
              type="date"
              value={end}
              min={start || undefined}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="mt-2 bg-[#0A0A0A] border-white/10 h-11"
              data-testid="report-end-date"
            />
          </div>
          <Button
            onClick={load}
            disabled={loading}
            className="bg-[#FF3B30] hover:bg-[#E02920] h-11 px-6"
            data-testid="apply-date-range"
          >
            {loading ? "Loading…" : "Apply"}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <PresetBtn label="Last 7 days" onClick={() => applyPreset(7)} testId="preset-7" />
          <PresetBtn label="Last 14 days" onClick={() => applyPreset(14)} testId="preset-14" />
          <PresetBtn label="Last 30 days" onClick={() => applyPreset(30)} testId="preset-30" />
          <PresetBtn label="Last 90 days" onClick={() => applyPreset(90)} testId="preset-90" />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Button onClick={load} variant="secondary" className="bg-[#1C1C1E] border border-white/10 text-white hover:bg-[#2C2C2E] h-11" data-testid="refresh-report"><RefreshCw size={14} className="mr-2" />Refresh</Button>
        <Button onClick={downloadCsv} variant="secondary" className="bg-[#1C1C1E] border border-white/10 text-white hover:bg-[#2C2C2E] h-11" data-testid="download-report-csv"><Download size={14} className="mr-2" />Download CSV</Button>
        <Button onClick={sendEmail} disabled={sending} className="bg-[#FF3B30] hover:bg-[#E02920] h-11" data-testid="email-report"><Mail size={14} className="mr-2" />{sending ? "Sending…" : "Email to me"}</Button>
      </div>

      {loading ? (<div className="text-center py-20 text-zinc-500">Building report…</div>) : report ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Employees" value={report.totals.employees} />
            <Stat label="Total Hours" value={`${report.totals.hours}h`} accent />
            <Stat label="Customer Cars" value={report.totals.customer_cars} />
            <Stat label="Dealership Tags" value={report.totals.dealership_tags} />
          </div>

          <div className="brand-surface rounded-2xl overflow-x-auto" data-testid="weekly-report-table">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-[#0A0A0A]">
                <tr className="text-overline text-left">
                  <th className="py-3 px-4">Employee</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4 text-right">Hours</th>
                  <th className="py-3 px-4 text-right">Cars detailed</th>
                  <th className="py-3 px-4 text-right">Tags posted</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.length === 0 && (<tr><td colSpan="5" className="py-10 text-center text-zinc-500">No data for this range.</td></tr>)}
                {report.rows.map((r) => (
                  <tr key={r.employee_id} className="border-t border-white/5" data-testid={`report-row-${r.employee_id}`}>
                    <td className="py-4 px-4 font-semibold">{r.name}</td>
                    <td className="py-4 px-4 text-zinc-400 text-xs">{r.email}</td>
                    <td className="py-4 px-4 text-right font-mono font-bold text-[#FF3B30]">{r.total_hours}h</td>
                    <td className="py-4 px-4 text-right font-mono">{r.customer_cars_detailed}</td>
                    <td className="py-4 px-4 text-right font-mono">{r.dealership_tags_posted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </AppShell>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`brand-surface rounded-xl p-4 ${accent ? "border-[#FF3B30]/30" : ""}`} data-testid={`report-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-overline">{label}</div>
      <div className={`font-mono font-bold text-3xl mt-3 ${accent ? "text-[#FF3B30]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function PresetBtn({ label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-[#0A0A0A] text-zinc-300 hover:text-white hover:border-[#FF3B30]/40 transition-colors"
      data-testid={testId}
    >
      {label}
    </button>
  );
}
